import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import type { PipelineSourceField, PipelineStep, StepConfig } from './types';
import PipelineStepCard from './PipelineStepCard';
import AddStepMenu from './AddStepMenu';

interface SortableStepProps {
  step: PipelineStep;
  index: number;
  sourceFields: PipelineSourceField[];
  onConfigChange: (stepId: string, config: Partial<StepConfig>) => void;
  onRemove: (stepId: string) => void;
}

const SortableStep: React.FC<SortableStepProps> = ({
  step,
  index,
  sourceFields,
  onConfigChange,
  onRemove,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: step.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 'auto',
    position: 'relative' as const,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <PipelineStepCard
        step={step}
        index={index}
        sourceFields={sourceFields}
        onConfigChange={onConfigChange}
        onRemove={onRemove}
        dragHandleProps={listeners}
        isDragging={isDragging}
      />
    </div>
  );
};

interface TransformationPipelineProps {
  steps: PipelineStep[];
  sourceFields: PipelineSourceField[];
  onConfigChange: (stepId: string, config: Partial<StepConfig>) => void;
  onRemove: (stepId: string) => void;
  onReorder: (activeId: string, overId: string) => void;
  onAddStep?: (functionName: string) => void;
}

const TransformationPipeline: React.FC<TransformationPipelineProps> = ({
  steps,
  sourceFields,
  onConfigChange,
  onRemove,
  onReorder,
  onAddStep,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      onReorder(active.id as string, over.id as string);
    }
  };

  if (steps.length === 0) {
    return (
      <Box
        sx={{
          py: 2.5,
          px: 2,
          textAlign: 'center',
          border: '2px dashed',
          borderColor: 'primary.light',
          borderRadius: 2,
          bgcolor: 'action.hover',
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          No transformation steps yet
        </Typography>
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 1.5 }}>
          Add a step to transform the source value before it reaches the target
        </Typography>
        {onAddStep && <AddStepMenu onAdd={onAddStep} />}
      </Box>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
    >
      <SortableContext items={steps.map((s) => s.id)} strategy={verticalListSortingStrategy}>
        <Stack spacing={0.75}>
          {steps.map((step, index) => (
            <SortableStep
              key={step.id}
              step={step}
              index={index}
              sourceFields={sourceFields}
              onConfigChange={onConfigChange}
              onRemove={onRemove}
            />
          ))}
        </Stack>
      </SortableContext>
    </DndContext>
  );
};

export default TransformationPipeline;
